<template>
  <div class="queue-display" :class="{ 'fullscreen': isFullscreen }">
    <!-- Header -->
    <div class="queue-header bg-primary text-white py-3 px-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h2 class="mb-0">{{ facilityName || 'Hospital Queue Display' }}</h2>
          <p class="mb-0 opacity-75">{{ currentDate }} | {{ currentTime }}</p>
        </div>
        <div class="d-flex align-items-center">
          <button 
            @click="toggleFullscreen" 
            class="btn btn-light btn-sm me-2"
            title="Toggle Fullscreen"
          >
            <i class="mdi" :class="isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen'"></i>
          </button>
          <button 
            @click="refresh" 
            class="btn btn-light btn-sm"
            :disabled="loading"
            title="Refresh"
          >
            <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- No Facility Selected -->
    <div v-if="!facilityID" class="queue-body d-flex align-items-center justify-content-center">
      <div class="text-center text-muted">
        <i class="mdi mdi-hospital-building font-size-64 d-block mb-3"></i>
        <h4>No Facility Selected</h4>
        <p>Please select a facility to display queue information</p>
      </div>
    </div>

    <!-- Queue Content -->
    <div v-else class="queue-body">
      <div class="container-fluid py-4">
        <div class="row">
          <!-- Now Serving Section -->
          <div class="col-lg-6 mb-4">
            <div class="card h-100 border-0 shadow-lg">
              <div class="card-header bg-success text-white py-3">
                <h4 class="mb-0 text-center">
                  <i class="mdi mdi-account-check me-2"></i>NOW SERVING
                </h4>
              </div>
              <div class="card-body p-4">
                <div v-if="nowServing.length === 0" class="text-center text-muted py-5">
                  <i class="mdi mdi-clock-outline font-size-48 d-block mb-3"></i>
                  <p class="mb-0">No patients being served currently</p>
                </div>
                <div v-else>
                  <div 
                    v-for="(item, index) in nowServing" 
                    :key="index" 
                    class="now-serving-item mb-3 p-4 bg-light rounded text-center"
                    :class="{ 'animate-pulse': index === 0 }"
                  >
                    <div class="queue-number display-1 fw-bold text-success mb-2">
                      {{ item.queueNo }}
                    </div>
                    <div class="patient-name h4 mb-1">{{ item.patientName }}</div>
                    <div class="location text-muted">
                      <i class="mdi mdi-map-marker me-1"></i>
                      {{ getLocationText(item.visitStatus) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Waiting Section -->
          <div class="col-lg-6 mb-4">
            <div class="card h-100 border-0 shadow-lg">
              <div class="card-header bg-warning text-dark py-3">
                <h4 class="mb-0 text-center">
                  <i class="mdi mdi-clock-outline me-2"></i>WAITING
                </h4>
              </div>
              <div class="card-body p-4">
                <div v-if="waiting.length === 0" class="text-center text-muted py-5">
                  <i class="mdi mdi-emoticon-happy font-size-48 d-block mb-3"></i>
                  <p class="mb-0">No patients waiting</p>
                </div>
                <div v-else class="waiting-list">
                  <div 
                    v-for="(item, index) in waiting.slice(0, 10)" 
                    :key="index" 
                    class="waiting-item d-flex justify-content-between align-items-center py-3 px-4 mb-2 bg-light rounded"
                  >
                    <div class="d-flex align-items-center">
                      <span class="queue-badge me-3 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center">
                        {{ item.queueNo }}
                      </span>
                      <span class="patient-name fw-semibold">{{ item.patientName }}</span>
                    </div>
                    <span class="badge" :class="statusBadgeClass(item.visitStatus)">
                      {{ getStatusText(item.visitStatus) }}
                    </span>
                  </div>
                  <div v-if="waiting.length > 10" class="text-center text-muted mt-3">
                    <small>+ {{ waiting.length - 10 }} more waiting...</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Queue by Department -->
        <div class="row">
          <!-- Triage -->
          <div class="col-md-3 mb-4">
            <div class="card border-0 shadow">
              <div class="card-body text-center py-4">
                <div class="avatar-lg mx-auto mb-3 bg-danger-subtle rounded-circle d-flex align-items-center justify-content-center">
                  <i class="mdi mdi-heart-pulse text-danger font-size-32"></i>
                </div>
                <h5 class="mb-1">TRIAGE</h5>
                <h2 class="display-4 fw-bold text-danger mb-0">{{ stats.triage || 0 }}</h2>
                <small class="text-muted">Waiting</small>
              </div>
            </div>
          </div>

          <!-- Consultation -->
          <div class="col-md-3 mb-4">
            <div class="card border-0 shadow">
              <div class="card-body text-center py-4">
                <div class="avatar-lg mx-auto mb-3 bg-primary-subtle rounded-circle d-flex align-items-center justify-content-center">
                  <i class="mdi mdi-doctor text-primary font-size-32"></i>
                </div>
                <h5 class="mb-1">CONSULTATION</h5>
                <h2 class="display-4 fw-bold text-primary mb-0">{{ stats.consultation || 0 }}</h2>
                <small class="text-muted">Waiting</small>
              </div>
            </div>
          </div>

          <!-- Laboratory -->
          <div class="col-md-3 mb-4">
            <div class="card border-0 shadow">
              <div class="card-body text-center py-4">
                <div class="avatar-lg mx-auto mb-3 bg-info-subtle rounded-circle d-flex align-items-center justify-content-center">
                  <i class="mdi mdi-flask text-info font-size-32"></i>
                </div>
                <h5 class="mb-1">LABORATORY</h5>
                <h2 class="display-4 fw-bold text-info mb-0">{{ stats.lab || 0 }}</h2>
                <small class="text-muted">Waiting</small>
              </div>
            </div>
          </div>

          <!-- Pharmacy -->
          <div class="col-md-3 mb-4">
            <div class="card border-0 shadow">
              <div class="card-body text-center py-4">
                <div class="avatar-lg mx-auto mb-3 bg-success-subtle rounded-circle d-flex align-items-center justify-content-center">
                  <i class="mdi mdi-pill text-success font-size-32"></i>
                </div>
                <h5 class="mb-1">PHARMACY</h5>
                <h2 class="display-4 fw-bold text-success mb-0">{{ stats.pharmacy || 0 }}</h2>
                <small class="text-muted">Waiting</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="queue-footer bg-dark text-white py-2 px-4">
      <div class="d-flex justify-content-between align-items-center">
        <small>Auto-refreshes every {{ refreshInterval }} seconds</small>
        <small v-if="lastUpdated">Last updated: {{ lastUpdated }}</small>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { execute, hospital } from "@/api";

export default {
  name: "QueueDisplay",

  props: {
    // Refresh interval in seconds
    refreshInterval: {
      type: Number,
      default: 30
    }
  },

  data() {
    return {
      loading: false,
      isFullscreen: false,
      facilityID: null,
      facilityName: '',
      currentDate: '',
      currentTime: '',
      lastUpdated: '',

      nowServing: [],
      waiting: [],

      stats: {
        triage: 0,
        consultation: 0,
        lab: 0,
        pharmacy: 0
      },

      refreshTimer: null,
      clockTimer: null
    }
  },

  mounted() {
    this.facilityID = sessionStorage.getItem("hospitalFacilityID");
    this.facilityName = sessionStorage.getItem("hospitalFacilityName");
    
    this.updateClock();
    this.clockTimer = setInterval(this.updateClock, 1000);

    if (this.facilityID) {
      this.loadQueueData();
    }

    this.startAutoRefresh();

    // Listen for fullscreen changes
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
  },

  beforeUnmount() {
    this.stopAutoRefresh();
    if (this.clockTimer) {
      clearInterval(this.clockTimer);
    }
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
  },

  methods: {
    updateClock() {
      const now = new Date();
      const dateOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      this.currentDate = now.toLocaleDateString('en-GB', dateOptions);
      this.currentTime = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    },

    loadQueueData() {
      this.getQueueManagement();
    },

    refresh() {
      this.facilityID = sessionStorage.getItem("hospitalFacilityID");
      this.facilityName = sessionStorage.getItem("hospitalFacilityName");
      
      if (this.facilityID) {
        this.loadQueueData();
      }
    },

    getQueueManagement() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getQueueManagement");
      data.append("facilityID", this.facilityID);
      data.append("queueType", "ALL");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, hospital)
        .then((res) => {
          this.loading = false;
          if (res.data.success) {
            const queue = res.data.data.queue || [];
            const stats = res.data.data.stats || {};

            // Separate now serving and waiting
            this.nowServing = queue.filter(item => 
              ['IN_TRIAGE', 'IN_CONSULTATION'].includes(item.visitStatus)
            ).slice(0, 3);

            this.waiting = queue.filter(item => 
              ['WAITING_TRIAGE', 'WAITING_CONSULTATION', 'WAITING_LAB', 'WAITING_PHARMACY'].includes(item.visitStatus)
            );

            // Update stats
            this.stats = {
              triage: stats.triageQueue || this.waiting.filter(i => i.visitStatus === 'WAITING_TRIAGE').length,
              consultation: stats.consultationQueue || this.waiting.filter(i => i.visitStatus === 'WAITING_CONSULTATION').length,
              lab: stats.labQueue || this.waiting.filter(i => i.visitStatus === 'WAITING_LAB').length,
              pharmacy: stats.pharmacyQueue || this.waiting.filter(i => i.visitStatus === 'WAITING_PHARMACY').length
            };

            this.lastUpdated = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
          }
        })
        .catch((e) => {
          this.loading = false;
          console.error("Error fetching queue data:", e.message);
        });
    },

    getLocationText(status) {
      const locations = {
        'IN_TRIAGE': 'Triage Room',
        'IN_CONSULTATION': 'Doctor\'s Room',
        'WAITING_LAB': 'Laboratory',
        'WAITING_PHARMACY': 'Pharmacy'
      };
      return locations[status] || 'Reception';
    },

    getStatusText(status) {
      const statuses = {
        'WAITING_TRIAGE': 'Triage',
        'IN_TRIAGE': 'In Triage',
        'WAITING_CONSULTATION': 'Consultation',
        'IN_CONSULTATION': 'With Doctor',
        'WAITING_LAB': 'Lab',
        'WAITING_PHARMACY': 'Pharmacy',
        'WAITING_PROCEDURE': 'Procedure'
      };
      return statuses[status] || status;
    },

    statusBadgeClass(status) {
      const classes = {
        'WAITING_TRIAGE': 'bg-danger',
        'IN_TRIAGE': 'bg-danger',
        'WAITING_CONSULTATION': 'bg-warning text-dark',
        'IN_CONSULTATION': 'bg-primary',
        'WAITING_LAB': 'bg-info',
        'WAITING_PHARMACY': 'bg-success',
        'WAITING_PROCEDURE': 'bg-secondary'
      };
      return classes[status] || 'bg-secondary';
    },

    toggleFullscreen() {
      if (!document.fullscreenElement) {
        this.$el.requestFullscreen().catch(err => {
          console.error("Error attempting fullscreen:", err);
        });
      } else {
        document.exitFullscreen();
      }
    },

    handleFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;
    },

    startAutoRefresh() {
      this.refreshTimer = setInterval(() => {
        if (this.facilityID) {
          this.loadQueueData();
        }
      }, this.refreshInterval * 1000);
    },

    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    }
  }
}
</script>

<style scoped>
.queue-display {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
}

.queue-display.fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
}

.queue-header {
  flex-shrink: 0;
}

.queue-body {
  flex-grow: 1;
  overflow-y: auto;
}

.queue-footer {
  flex-shrink: 0;
}

.now-serving-item {
  transition: all 0.3s ease;
}

.animate-pulse {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(52, 195, 143, 0.4);
  }
  50% {
    box-shadow: 0 0 0 15px rgba(52, 195, 143, 0);
  }
}

.queue-badge {
  width: 40px;
  height: 40px;
  font-size: 16px;
  font-weight: bold;
}

.waiting-item {
  transition: background-color 0.2s;
}

.waiting-item:hover {
  background-color: #e9ecef !important;
}

.avatar-lg {
  width: 80px;
  height: 80px;
}

.bg-danger-subtle { background-color: rgba(244, 106, 106, 0.15); }
.bg-primary-subtle { background-color: rgba(85, 110, 230, 0.15); }
.bg-info-subtle { background-color: rgba(80, 165, 241, 0.15); }
.bg-success-subtle { background-color: rgba(52, 195, 143, 0.15); }

.display-1 {
  font-size: 4rem;
}

.display-4 {
  font-size: 2.5rem;
}

.font-size-32 {
  font-size: 32px;
}

.font-size-48 {
  font-size: 48px;
}

.font-size-64 {
  font-size: 64px;
}
</style>
